import {
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
  differenceInYears,
  format,
  isBefore,
  isValid,
  parseISO,
  subDays,
  subHours,
  subMinutes,
  subSeconds,
  subYears,
} from 'date-fns';

export const dateFormat = 'MM/dd/yyyy';
export const dateTimeFormat = 'MM/dd/y HH:mm';
export const timeFormat = 'HH:mm';

export function toDate(date: Date | number | string | null | undefined): Date | null | undefined {
  if (date instanceof Date) {
    date = date;
  } else if (typeof date === 'string') {
    date = parseISO(date);
  } else if (typeof date === 'number') {
    date = new Date(date);
  }
  return date;
}

export function formatDate(
  dt: Date | number | string | null | undefined,
  formatString: string,
): string {
  const newDate = toDate(dt);

  if (newDate != null) {
    return format(newDate, formatString);
  } else {
    return '';
  }
}

export function isValidDateString(date: string): boolean {
  return isValid(new Date(Date.parse(date)));
}

export function distanceInWordsShort(
  date: Date,
  dateToCompare: Date,
  {
    includeSeconds,
  }: {
    includeSeconds?: boolean;
  } = {
    includeSeconds: false,
  },
): string {
  const msDiff = Math.abs(differenceInMilliseconds(date, dateToCompare));
  if (msDiff === 0) {
    return includeSeconds === true ? '0s' : '0m';
  }

  if (includeSeconds === true && msDiff < 1000) {
    return '<1s';
  } else if (includeSeconds === false && msDiff < 60000) {
    return '<1m';
  }

  // eslint-disable-next-line prefer-const
  let { smaller, larger } = isBefore(date, dateToCompare)
    ? {
        smaller: new Date(date),
        larger: new Date(dateToCompare),
      }
    : {
        smaller: new Date(dateToCompare),
        larger: new Date(date),
      };
  const values: [number, string][] = [];

  const years = differenceInYears(larger, smaller);
  larger = subYears(larger, years);
  values.push([years, 'y']);
  const days = differenceInDays(larger, smaller);
  larger = subDays(larger, days);
  values.push([days, 'd']);
  if (years === 0) {
    const hours = differenceInHours(larger, smaller);
    larger = subHours(larger, hours);
    values.push([hours, 'h']);
    if (days === 0) {
      const minutes = differenceInMinutes(larger, smaller);
      larger = subMinutes(larger, minutes);
      values.push([minutes, 'm']);
    }
  }

  if (includeSeconds === true) {
    const seconds = differenceInSeconds(larger, smaller);
    larger = subSeconds(larger, seconds);
    values.push([seconds, 's']);
  }
  const stringValues = values
    .map(([val, suffix]) => (val > 0 ? `${val}${suffix}` : null))
    .filter((x) => x != null);
  if (stringValues.length > 0) {
    return stringValues.join(' ');
  } else {
    throw new Error('cannot find difference');
  }
}

export function withTimezone(date: Date, timezoneCd: string, locale: string = 'en-US') {
  const tzDt = date.toLocaleString(locale, { timeZone: timezoneCd });
  return new Date(tzDt);
}
